import React from 'react';
import type { IntlShape } from 'react-intl';

import type {
  CalibrationComment,
  ThreadedCalibrationComment,
  UpdateCalibrationCommentPayload,
} from '../types';

import { CommentComposer, type CommentDraft } from './CommentComposer';
import { CommentEditForm } from './CommentEditForm';
import { CommentItem } from './CommentItem';

export type CommentThreadProps = {
  comments: ThreadedCalibrationComment[];
  expandedThreads: Set<string>;
  activeReplyTarget: string | null;
  replyText: string;
  onReplyTextChange: (value: string) => void;
  replyIsPrivate: boolean;
  onReplyIsPrivateChange: (value: boolean) => void;
  replyError: string | null;
  isSubmittingReply: boolean;
  getReplyDraftKey: (parentCommentId: string) => string;
  onReplyDraftRestored: (draft: CommentDraft) => void;
  onReplyPendingDraftSaveChange?: (hasPendingDraftSave: boolean) => void;
  onOpenReplyComposer: (comment: CalibrationComment) => void;
  onCloseReplyComposer: () => void;
  onSubmitReply: (
    comment: ThreadedCalibrationComment,
    onPosted: () => void
  ) => void;
  onToggleThread: (threadKey: string) => void;
  onToggleVisibility?: (comment: CalibrationComment) => void;
  editingCommentId: string | null;
  editError: string | null;
  isSubmittingEdit: boolean;
  onOpenEditor?: (comment: CalibrationComment) => void;
  onCloseEditor: () => void;
  onSubmitEdit: (
    comment: ThreadedCalibrationComment,
    payload: UpdateCalibrationCommentPayload
  ) => void;
  onEditDirtyChange?: (isDirty: boolean) => void;
  formatMessage: IntlShape['formatMessage'];
  variant?: 'popover' | 'modal';
  canAddComment?: boolean;
  currentUserId?: number;
  isViewAdmin?: boolean;
  highlightedCommentId?: number;
};

const countAllReplies = (comment: ThreadedCalibrationComment): number => {
  const replies = comment.replies ?? [];
  return replies.reduce(
    (total, reply) => total + 1 + countAllReplies(reply),
    0
  );
};

export const CommentThread: React.FC<CommentThreadProps> = ({
  comments,
  expandedThreads,
  activeReplyTarget,
  replyText,
  onReplyTextChange,
  replyIsPrivate,
  onReplyIsPrivateChange,
  replyError,
  isSubmittingReply,
  getReplyDraftKey,
  onReplyDraftRestored,
  onReplyPendingDraftSaveChange,
  onOpenReplyComposer,
  onCloseReplyComposer,
  onSubmitReply,
  onToggleThread,
  onToggleVisibility,
  editingCommentId,
  editError,
  isSubmittingEdit,
  onOpenEditor,
  onCloseEditor,
  onSubmitEdit,
  onEditDirtyChange,
  formatMessage,
  variant = 'modal',
  canAddComment = true,
  currentUserId,
  isViewAdmin = false,
  highlightedCommentId,
}) => {
  const isPopover = variant === 'popover';
  const canReply = canAddComment && !isPopover;

  const renderComment = (
    comment: ThreadedCalibrationComment,
    depth: number,
    rootThreadKey: string
  ) => {
    const commentId = String(comment.id);
    const isReplying = activeReplyTarget === commentId;

    // Use flattened replies for Slack-style threading (all replies at depth 1)
    const replies = comment.replies ?? [];
    const repliesAvailable = countAllReplies(comment);
    const isRootComment = depth === 0;
    const threadIsExpanded = expandedThreads.has(rootThreadKey);

    const isAuthor = !!currentUserId && comment.author?.id === currentUserId;
    const canToggleVisibility = isViewAdmin || isAuthor;
    const canEdit = canAddComment && !isPopover && isAuthor;
    const isEditing = canEdit && editingCommentId === commentId;

    return (
      <div key={`calibration-comment-${commentId}`}>
        <CommentItem
          comment={comment}
          depth={depth}
          isReplying={isReplying}
          onReply={canReply ? onOpenReplyComposer : undefined}
          onToggleThread={isRootComment ? onToggleThread : undefined}
          onToggleVisibility={onToggleVisibility}
          isThreadExpanded={threadIsExpanded}
          repliesAvailable={repliesAvailable}
          rootThreadKey={rootThreadKey}
          formatMessage={formatMessage}
          variant={variant}
          canToggleVisibility={canToggleVisibility}
          onEdit={canEdit ? onOpenEditor : undefined}
          editForm={
            isEditing ? (
              <CommentEditForm
                comment={comment}
                editError={editError}
                isSubmitting={isSubmittingEdit}
                onSubmit={(payload) => onSubmitEdit(comment, payload)}
                onCancel={onCloseEditor}
                onDirtyChange={onEditDirtyChange}
                formatMessage={formatMessage}
              />
            ) : undefined
          }
          isHighlighted={comment.id === highlightedCommentId}
        />
        {canReply && isReplying && (
          <CommentComposer
            isOpen
            isReply
            composerContent={replyText}
            onComposerContentChange={onReplyTextChange}
            composerIsPrivate={replyIsPrivate}
            onComposerIsPrivateChange={onReplyIsPrivateChange}
            composerError={replyError}
            isSubmitting={isSubmittingReply}
            onCancel={onCloseReplyComposer}
            onSubmit={(onPosted) => onSubmitReply(comment, onPosted)}
            draftKey={getReplyDraftKey(commentId)}
            onDraftRestored={onReplyDraftRestored}
            onPendingDraftSaveChange={onReplyPendingDraftSaveChange}
          />
        )}
        {threadIsExpanded && repliesAvailable > 0 && (
          <div
            className="calibration-thread-replies mt-3 ms-3 ps-3 d-flex flex-column gap-3"
            style={{ borderLeft: '6px solid var(--bs-border-color, #dee2e6)' }}
          >
            {replies.map((reply) => renderComment(reply, 1, rootThreadKey))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="calibration-thread-list d-flex flex-column gap-3">
      {comments.map((comment) =>
        renderComment(comment, 0, String(comment.thread_id))
      )}
    </div>
  );
};
